import { MaintenanceTask } from "@/lib/types";
import { Drawer, DrawerContent, DrawerHeader } from "@/components/ui/drawer";
import { useTranslation } from "react-i18next";
import { X } from "@phosphor-icons/react";
import useVessel from "@/store/use-vessel";
import { Checkbox } from "@/components/ui/checkbox";
import {
  useAssignTask,
  useUnassignTask,
} from "@/services/mutations/maintenance";
import { toast } from "@/hooks/use-toast";
import { useState } from "react";

const ManageAssigneesDrawer = ({
  open,
  setOpen,
  task,
  vesselId,
  onUpdated,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  task: MaintenanceTask;
  vesselId: string;
  onUpdated: () => void;
}) => {
  const { t } = useTranslation();
  const { selectedVessel } = useVessel();
  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set());

  const crewMembers =
    selectedVessel?.crew?.filter(
      (member) => member.isActive && member.userId
    ) ?? [];

  const assignedUserIds = new Set(
    task.taskDetail.assignees?.map((a) => a.id) ?? []
  );

  const { mutate: assignTask } = useAssignTask({
    onSuccess: () => {
      onUpdated();
    },
    onError: (error) => {
      toast({
        title: t("error_message.title"),
        description:
          error.response?.data?.message ??
          t("error_message.something_went_wrong"),
        variant: "destructive",
      });
    },
    onSettled: (_data, _error, variables) => {
      setPendingIds((prev) => {
        const next = new Set(prev);
        next.delete(variables.userId);
        return next;
      });
    },
  });

  const { mutate: unassignTask } = useUnassignTask({
    onSuccess: () => {
      onUpdated();
    },
    onError: (error) => {
      toast({
        title: t("error_message.title"),
        description:
          error.response?.data?.message ??
          t("error_message.something_went_wrong"),
        variant: "destructive",
      });
    },
    onSettled: (_data, _error, variables) => {
      setPendingIds((prev) => {
        const next = new Set(prev);
        next.delete(variables.userId);
        return next;
      });
    },
  });

  const handleToggle = (userId: string) => {
    if (pendingIds.has(userId)) return;

    setPendingIds((prev) => new Set(prev).add(userId));

    if (assignedUserIds.has(userId)) {
      unassignTask({
        vesselId,
        taskId: task.id,
        userId,
        version: task.version,
        detailVersion: task.taskDetail.version,
      });
    } else {
      assignTask({
        vesselId,
        taskId: task.id,
        userId,
        version: task.version,
        detailVersion: task.taskDetail.version,
      });
    }
  };

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerContent>
        <DrawerHeader
          title={t("title.manage_assignees")}
          leftButton={<span className="w-[18px]" />}
          rightButton={
            <button type="button" onClick={() => setOpen(false)}>
              <X color="#E3EDFC" size={18} />
            </button>
          }
        />

        <p className="mx-auto max-w-[240px] text-center text-sm font-medium leading-[18px] text-[#98B2D3]">
          {t("description.manage_assignees")}
        </p>

        <div className="mt-5 space-y-[10px]">
          {crewMembers.length === 0 ? (
            <p className="text-center text-sm text-[#98B2D3]">
              {t("message.no_crew_members")}
            </p>
          ) : (
            crewMembers
              .filter((m) => m.userId)
              .map((member) => {
                const isAssigned = assignedUserIds.has(member.userId!);
                const isPending = pendingIds.has(member.userId!);

                return (
                  <button
                    type="button"
                    key={member.id}
                    disabled={isPending}
                    onClick={() => handleToggle(member.userId!)}
                    className="flex w-full flex-row items-center justify-between rounded-lg bg-[#1FB3CC1F] px-[15px] py-[10px] shadow-[inset_0px_1px_2px_0px_#0C0C2633] backdrop-blur-lg disabled:opacity-50"
                  >
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={isAssigned}
                        onCheckedChange={() => handleToggle(member.userId!)}
                        disabled={isPending}
                      />
                      <div className="flex flex-col items-start">
                        <span className="text-[15px] leading-5 text-white">
                          {member.fullName}
                        </span>
                        <span className="text-[13px] leading-4 text-[#FFFFFFA8]">
                          {member.role}
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default ManageAssigneesDrawer;
